import { useMemo } from 'react';
import { Button, Column, Pressable, Text } from '~/commonComponents';
import { Colors } from '~/themes/colors';
import CloseIcon from '~/images/close-icon.svg';
import { TEducationMap } from '~/domain/FavoriteProfessionals/favoriteProfessionalsTypes';
import { EducationUtils } from '~/utils/EducationUtils';
import { UserProfession } from '~/enums/UserProfession';

interface IViewSkillsScreenProps {
  fullName: string;
  profession: string;
  professionCode: string;
  education: TEducationMap;
  onBack: () => void;
  onClose: () => void;
}

const isDnDa = (code: string) => [UserProfession.DN, UserProfession.DA].includes(code);

const isRdhFd = (code: string) => [UserProfession.RDH, UserProfession.FD].includes(code);

export const ViewSkillsScreen = ({
  fullName,
  profession,
  professionCode,
  education,
  onBack,
  onClose,
}: IViewSkillsScreenProps) => {
  // Try exact professionCode first, then fall back to first available entry
  const resolvedCode = education[professionCode]
    ? professionCode
    : (Object.keys(education)[0] ?? professionCode);
  const educationEntry = education[resolvedCode];
  const specialtiesAndProcedures = useMemo(
    () => educationEntry?.specialtiesAndProcedures ?? {},
    [educationEntry],
  );

  // DN/DA: specialty keys map to procedure arrays
  const dnDaSpecialties = useMemo(() => {
    if (!isDnDa(resolvedCode)) return [];
    return Object.entries(specialtiesAndProcedures);
  }, [resolvedCode, specialtiesAndProcedures]);

  // RDH/FD: procedures are top-level keys prefixed with "procedure_"
  const rdhFdProcedures = useMemo(() => {
    if (!isRdhFd(resolvedCode)) return [];
    return EducationUtils.getProceduresOfProfession({
      specialtiesAndProcedures,
      profession: resolvedCode,
      specialties: [],
    }) as string[];
  }, [resolvedCode, specialtiesAndProcedures]);

  return (
    <Column flex={1} position="relative">
      <Pressable onPress={onClose} style={{ position: 'absolute', right: 20, top: 20, zIndex: 1 }}>
        <img
          src={CloseIcon}
          style={{ cursor: 'pointer', height: 24, width: 24 }}
          alt="close_icon"
        />
      </Pressable>

      <Column marginBottom={24} marginTop={54}>
        <Text bold fontSize={18} textAlign="center">
          Professional Skills
        </Text>
      </Column>

      <Column paddingHorizontal={48} marginBottom={16}>
        <Text fontSize={20}>
          <Text bold fontSize={20} color={Colors.neutral_500}>
            {fullName}
          </Text>
          <Text fontSize={18} color={Colors.neutral_500}>
            {` - ${profession}`}
          </Text>
        </Text>
      </Column>

      <Column
        flex={1}
        flexBasis={0}
        overflowY="auto"
        paddingHorizontal={48}
        style={{ scrollbarWidth: 'thin' }}
      >
        {isDnDa(resolvedCode) &&
          dnDaSpecialties.map(([specialtyKey, procedures]) => (
            <Column key={specialtyKey} marginBottom={16}>
              <Text bold fontSize={18} color={Colors.primary_500}>
                {EducationUtils.removeSpecialtyPrefix(specialtyKey)}
              </Text>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  marginTop: 8,
                }}
              >
                {procedures.map((proc: string) => (
                  <Text key={proc} fontSize={16} color={Colors.neutral_600}>
                    {`\u2022  ${EducationUtils.removeProcedurePrefix(proc)}`}
                  </Text>
                ))}
              </div>
            </Column>
          ))}

        {isRdhFd(resolvedCode) && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {rdhFdProcedures.map((name: string) => (
              <Text key={name} fontSize={16} color={Colors.neutral_600}>
                {`\u2022  ${EducationUtils.removeProcedurePrefix(name)}`}
              </Text>
            ))}
          </div>
        )}
      </Column>

      <Button
        text="Back to Invite"
        style={{
          marginInline: 'auto',
          width: 152,
          marginBottom: 32,
          marginTop: 24,
        }}
        onClick={onBack}
      />
    </Column>
  );
};
